import { useEffect, useRef, useState } from 'react';
import { useExam } from '../context/ExamContext';
import { IncidentType } from '../types/domain';
import { makeIncident } from '../utils/incidents';

const SAMPLE_INTERVAL_MS = 200;
const METRICS_INTERVAL_MS = 1000;

export function useAudioMonitor(stream?: MediaStream, startedAt?: string, enabled = true) {
  const { addIncident, updateMetrics } = useExam();
  const [level, setLevel] = useState(0);
  const activeIncident = useRef<IncidentType>();

  useEffect(() => {
    if (!stream || !enabled) return;

    const AudioCtor = window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioCtor) return;

    const context = new AudioCtor();
    const analyser = context.createAnalyser();
    analyser.fftSize = 512;
    const source = context.createMediaStreamSource(stream);
    source.connect(analyser);
    const data = new Uint8Array(analyser.frequencyBinCount);
    let sustainedSpeechSamples = 0;
    let sustainedWhisperSamples = 0;
    let lastMetricsAt = 0;

    const sample = () => {
      analyser.getByteFrequencyData(data);
      let total = 0;
      for (const value of data) total += value;
      const normalized = Math.min(100, Math.round((total / data.length) * 1.8));
      const now = Date.now();

      if (now - lastMetricsAt >= METRICS_INTERVAL_MS) {
        lastMetricsAt = now;
        setLevel(normalized);
        updateMetrics({ noiseLevel: normalized, microphoneHealth: normalized > 80 ? 'warning' : 'good' });
      }

      sustainedSpeechSamples = normalized > 18 && normalized < 70 ? sustainedSpeechSamples + 1 : 0;
      sustainedWhisperSamples = normalized > 8 && normalized < 16 ? sustainedWhisperSamples + 1 : 0;
      let detected: IncidentType | undefined;
      let confidence = normalized;
      let severity: 'low' | 'medium' | 'high' = 'medium';
      if (normalized > 82) {
        detected = 'loud_noise';
        severity = 'high';
      } else if (sustainedSpeechSamples >= 10) {
        detected = 'talking';
        confidence = 72;
      } else if (sustainedWhisperSamples >= 8) {
        detected = 'whispering';
        confidence = 58;
        severity = 'low';
      }

      if (detected && detected !== activeIncident.current) {
        const elapsed = startedAt ? Math.round((now - new Date(startedAt).getTime()) / 1000) : 0;
        addIncident(makeIncident(detected, confidence, severity, elapsed, 'audio'));
      }
      activeIncident.current = detected;
    };

    const interval = window.setInterval(sample, SAMPLE_INTERVAL_MS);
    return () => {
      window.clearInterval(interval);
      source.disconnect();
      void context.close();
      activeIncident.current = undefined;
    };
  }, [addIncident, enabled, startedAt, stream, updateMetrics]);

  return level;
}
